import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useUserData } from "../../context/UserDataFetcher";
import DashboardSidebar from "./DashboardSidebar";
import { getAuth, signOut } from "firebase/auth";
import { app } from "../../firebaseConfig";
import { useHistory } from "react-router";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import { useUserControls } from "../../context/UserControlsProvider";
import DeleteAccountDialog from "../Account/DeleteAccountDialog";
import { useDemoMode } from "../../context/DemoModeProvider";

type DashboardBarProps = {
  onNavigate?: (route: string) => void;
};


const DashboardBar: React.FC<DashboardBarProps> = ({ onNavigate }) => {
  const theme: any = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const userData = useUserData();
  const history = useHistory();

  const isManufacturerAdmin =
    userData?.role === "manufacturer" &&
    (userData?.isAdmin || userData?.isManufacturerPrincipal);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [accountAnchorEl, setAccountAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const [deleteAccountOpen, setDeleteAccountOpen] = React.useState(false);
  const { showSnackbar } = useUserControls();
  const {
    demoMode,
    loadingDemo,
    savingDemo,
    setDemoModeEnabled,
  } = useDemoMode();

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleClick = (route: string) => {
    handleCloseMenu();
    if (onNavigate) onNavigate(route);
  };

  const handleSignOut = async () => {
    try {
      await signOut(getAuth(app));
      history.push("/");
    } catch (error) {
      console.error("Error signing out: ", error);
      showSnackbar?.({
        message: "Failed to sign out. Please try again.",
        severity: "error",
        duration: 5000,
      });
    }
  };

  const navOptions: { label: string; action: () => void}[] = [
    // Keep only Logout here; Account will open a dropdown with settings
    { label: "Logout", action: handleSignOut },
  ];

  const openAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAccountAnchorEl(event.currentTarget);
  };
  const closeAccountMenu = () => setAccountAnchorEl(null);

  const permissionLabel = React.useMemo(() => {
    if (userData?.role === "developer" || userData?.isDev) return "Developer";
    if (userData?.isAdmin) return "Admin";
    if (userData?.isManufacturerPrincipal) return "Principal";
    return "User";
  }, [userData?.role, userData?.isDev, userData?.isAdmin, userData?.isManufacturerPrincipal]);

  return (
    <>
      <AppBar
        position="static"
        color={(theme as any).brand}
        sx={{
          borderRadius: 0,
          background: `linear-gradient(
            20deg,
            ${theme.background} 0%,
            ${theme.background} 400%
          )`,
          color: theme.brand,
          zIndex: 1000000000,
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: 2, height: "6rem", borderBottom: 1, borderBottomColor: 'divider' }}>
          <Typography variant="h4" noWrap sx={{ flexShrink: 0, mr: 3 }}>
            {userData?.manufacturerId || userData?.dealerId} Dashboard
          </Typography>

          {/* spacer */}
          <Box sx={{ flex: 1 }} />

          {/* desktop buttons */}
          {!isNarrow ? (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <ThemeToggle />
              <IconButton color="inherit" onClick={openAccountMenu} aria-label="account settings">
                <SettingsIcon />
              </IconButton>
              <Menu
                anchorEl={accountAnchorEl}
                open={Boolean(accountAnchorEl)}
                onClose={closeAccountMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem disableRipple disableGutters>
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(userData?.email || "Unknown user").toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Role: {(userData?.role || "—").toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Permissions: {permissionLabel.toUpperCase()}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem disableRipple disableGutters>
                  <Box sx={{ px: 2, py: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={demoMode}
                          disabled={loadingDemo || savingDemo}
                          onChange={async (e) => {
                            const enabled = e.target.checked;
                            // Confirmation when turning OFF
                            if (demoMode && !enabled) {
                              const ok = window.confirm(
                                "Are you sure you want to turn off training sandbox mode?"
                              );
                              if (!ok) {
                                // revert
                                return;
                              }
                            }

                            try {
                              await setDemoModeEnabled(enabled);
                              showSnackbar?.({
                                message: `Training sandbox ${enabled ? "enabled" : "disabled"}`,
                                severity: "success",
                                duration: 3000,
                              });
                            } catch (err) {
                              console.error("Failed to update demoMode", err);
                              showSnackbar?.({
                                message: "Failed to update training sandbox",
                                severity: "error",
                              });
                            }
                          }}
                        />
                      }
                      label="Training Sandbox"
                    />
                  </Box>
                </MenuItem>
                {isManufacturerAdmin && (
                  <MenuItem
                    onClick={() => {
                      history.push("/admin/api-docs");
                      closeAccountMenu();
                    }}
                    sx={{ px: 2, py: 1 }}
                  >
                    API Documentation
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    closeAccountMenu();
                    setDeleteAccountOpen(true);
                  }}
                  sx={{ px: 2, py: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "error.main", fontWeight: 600 }}
                  >
                    Delete Account
                  </Typography>
                </MenuItem>
              </Menu>
              <Button
                onClick={handleSignOut}
                sx={{ textTransform: "none" }}
              >
                Logout
              </Button>
            </Box>
          ) : (
            // collapsed menu for narrow widths
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                aria-controls={anchorEl ? "dashboard-menu" : undefined}
                aria-haspopup="true"
                onClick={handleOpenMenu}
                size="large"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="dashboard-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {navOptions.map((opt) => (
                  <MenuItem
                    key={opt.label}
                    onClick={() => opt.action()}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <DeleteAccountDialog
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        onDeleted={async () => {
          await handleSignOut();
        }}
      />
    </>
  );
};

export default DashboardBar;
